import React from 'react';
import { UserCheck, PhoneCall, ClipboardCheck, CalendarCheck } from 'lucide-react';
import { type HowItWorksContent } from '../lib/variants';

interface HowItWorksProps {
  content: HowItWorksContent;
}

const icons = [
  <UserCheck className="h-8 w-8 md:h-10 md:w-10" key="uc" />,
  <PhoneCall className="h-8 w-8 md:h-10 md:w-10" key="pc" />,
  <ClipboardCheck className="h-8 w-8 md:h-10 md:w-10" key="cc" />,
  <CalendarCheck className="h-8 w-8 md:h-10 md:w-10" key="cal" />,
];

const HowItWorks: React.FC<HowItWorksProps> = ({ content }) => {
  return (
    <section id="jak-to-dziala" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 text-sm font-semibold mb-4 border border-cyan-100">
            {content.badge}
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            {content.headline}
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            {content.subheadline}
          </p>
        </div>

        <div className="relative grid md:grid-cols-4 gap-8 md:gap-4">
          <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-cyan-200 -z-10" aria-hidden="true" />

          {content.steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center text-center group relative">
              <div className="relative mb-6">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-white border-4 border-cyan-100 text-cyan-600 rounded-full flex items-center justify-center shadow-xl shadow-cyan-500/10 group-hover:border-cyan-400 group-hover:scale-110 transition-all duration-300 relative z-10">
                  {icons[index]}
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-2.5 py-1 rounded-full border-2 border-white shadow-sm whitespace-nowrap z-20">
                  {step.time}
                </div>
              </div>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed px-2">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-2xl bg-slate-950 text-white p-8 text-center max-w-3xl mx-auto">
          <p className="text-lg font-semibold text-slate-300 mb-2">{content.bottomDescription}</p>
          <p className="text-2xl font-extrabold text-cyan-400">
            {content.bottomTitle}
          </p>
          <p className="mt-3 text-slate-400 text-sm">
            {content.bottomNote}
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
